import React, { useEffect, useState } from "react";
import {
  FaArrowCircleUp,
  FaFileInvoiceDollar,
  FaHandshake,
  FaUser,
  FaExternalLinkSquareAlt,
  FaCalendarAlt,
  FaChartLine,
  FaChartBar,
} from "react-icons/fa";
import axiosInstance from "../config/axios";
import { useRole } from "../context/RoleContext";
import {
  computeDashboardStats,
  formatStatMoney,
} from "../utils/dashboardStatsUtils";
import {
  getUpcomingBirthdays,
  getUpcomingLicenseExpiries,
} from "../utils/dashboardAgentAlertUtils";
import DashboardAgentAlerts from "./DashboardAgentAlerts";

const CARD_META = [
  {
    key: "grossCommissionReceivedYtd",
    label: "Gross Commission Received (YTD)",
    money: true,
    icon: FaArrowCircleUp,
    accent: "border-l-violet-500",
    iconWrap: "bg-violet-500",
  },
  {
    key: "pendingReceipts",
    label: "Pending Receipts",
    money: true,
    icon: FaFileInvoiceDollar,
    accent: "border-l-cyan-500",
    iconWrap: "bg-cyan-500",
  },
  {
    key: "lastReconciliationDone",
    label: "Last Reconciliation Done",
    icon: FaHandshake,
    accent: "border-l-fuchsia-500",
    iconWrap: "bg-fuchsia-500",
  },
  {
    key: "activeAgents",
    label: "Number of active agent",
    icon: FaUser,
    accent: "border-l-red-500",
    iconWrap: "bg-red-500",
  },
  {
    key: "tradesOpen",
    label: "Trades Open",
    icon: FaExternalLinkSquareAlt,
    accent: "border-l-indigo-500",
    iconWrap: "bg-indigo-500",
  },
  {
    key: "tradesClosingThisMonth",
    label: "Trades Closing this Month",
    icon: FaCalendarAlt,
    accent: "border-l-green-500",
    iconWrap: "bg-green-500",
  },
  {
    key: "tradesClosingToday",
    label: "Trades Closing Today",
    icon: FaChartLine,
    accent: "border-l-yellow-400",
    iconWrap: "bg-yellow-400",
  },
  {
    key: "tradesClosedYtd",
    label: "Trades Closed(YTD)",
    icon: FaChartBar,
    accent: "border-l-orange-500",
    iconWrap: "bg-orange-500",
  },
];

const emptyStats = {
  grossCommissionReceivedYtd: 0,
  pendingReceipts: 0,
  lastReconciliationDone: "",
  activeAgents: 0,
  tradesOpen: 0,
  tradesClosingThisMonth: 0,
  tradesClosingToday: 0,
  tradesClosedYtd: 0,
};

export default function DashboardStatsCards() {
  const { isAgent, appUser } = useRole();
  const [stats, setStats] = useState(emptyStats);
  const [birthdays, setBirthdays] = useState([]);
  const [expiries, setExpiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const cards = isAgent
    ? CARD_META.filter(
        (card) =>
          card.key !== "lastReconciliationDone" && card.key !== "activeAgents",
      )
    : CARD_META;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [tradesRes, agentsRes, reconRes] = await Promise.all([
          axiosInstance.get("/trades"),
          isAgent
            ? Promise.resolve({ data: [] })
            : axiosInstance.get("/agents").catch(() => ({ data: [] })),
          isAgent
            ? Promise.resolve({ data: { date: null } })
            : axiosInstance
                .get("/reconciliation-settings/summary/latest")
                .catch(() => ({ data: { date: null } })),
        ]);
        if (cancelled) return;
        const agents = agentsRes.data || [];
        setStats(
          computeDashboardStats(
            tradesRes.data || [],
            agents,
            reconRes.data?.date || null,
            isAgent ? appUser : null,
          ),
        );
        if (isAgent) {
          setBirthdays([]);
          setExpiries([]);
        } else {
          setBirthdays(getUpcomingBirthdays(agents, 7));
          setExpiries(getUpcomingLicenseExpiries(agents, 30));
        }
      } catch (err) {
        console.error("Error loading dashboard stats:", err);
        if (!cancelled) {
          setStats(emptyStats);
          setBirthdays([]);
          setExpiries([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isAgent, appUser]);

  return (
    <div className="px-6 pb-6">
      <div className="flex flex-col xl:flex-row gap-3 items-stretch">
        <div
          className={`flex-1 grid grid-cols-1 sm:grid-cols-2 ${
            isAgent ? "xl:grid-cols-3" : "xl:grid-cols-4"
          } gap-3`}
        >
        {cards.map((card) => {
          const Icon = card.icon;
          const raw = stats[card.key];
          const value = card.money
            ? formatStatMoney(raw)
            : raw === 0 || raw
              ? String(raw)
              : "";
          return (
            <div
              key={card.key}
              className={`bg-white rounded-lg shadow-sm border border-gray-100 border-l-4 ${card.accent} px-3 py-2 flex items-center justify-between`}
            >
              <div className="pr-2 min-w-0">
                <p className="text-xs text-gray-500 leading-snug">{card.label}</p>
                <p className="mt-0.5 text-lg font-bold text-gray-900 truncate">
                  {loading ? "…" : value}
                </p>
              </div>
              <div
                className={`w-7 h-7 rounded-md ${card.iconWrap} text-white flex items-center justify-center flex-shrink-0`}
              >
                {Icon ? <Icon className="text-xs" /> : null}
              </div>
            </div>
          );
        })}
        </div>
        {!isAgent && (
        <DashboardAgentAlerts
          birthdays={birthdays}
          expiries={expiries}
          loading={loading}
        />
        )}
      </div>
    </div>
  );
}
